import {
    ExposeTransport,
    PropertyOverview,
    PropertyDetails as PropertyDetailsGeneralTransport,
    SurroundingLocation,
    Location,
    PropertyObjectTransport,
    CustomerObjectsTransport,
    ListingConfigTransport,
} from '../../transports/property-details-page-transport'
import { Gallery, GalleryImages } from './gallery-model'
import { PropertyDescription, PropertyDescriptionTransport } from './property-description-model'
import { TranslationFunction, FormatDateFunction, RoutingFunction } from '../../common/types'
import { Trust } from './trust-model'
import { PropertyType, PropertyTypeConfig } from './property-type-model'
import { FormattedStats, Stats } from './stats-model'
import { PropertyDetailsGeneralModel } from './property-details-general-model'
import { ListingConfigModel } from './listing-config-model'
import { Surrounding } from './surrounding-model'
import { Accessibility, FormattedAccessibilityTransport } from './accessibility-model'
import { FormattedSuitableFeatures, FormattedSuitablesMap, SuitablesModel } from '../common/suitables-model'
import { FormattedSuitedMap, Suited } from './suited-model'
import { FeatFilter, FormattedFeatFilterMap } from './featfilter-model'
import { Features, FeaturesFormatter } from '../common/features-model'
import { FormattedRoomsResult, Rooms } from './rooms-model'
import { ContactFormatter, FormattedContactDetails } from './contact-model'
import { BookingType, FormattedBookingTypeData } from './booking-type-model'
import { FormattedRatingTransport, Reviews } from './reviews-model'
import { FormattedTravelNote, TravelNotes } from './notes-model'
import { BookingCancellationPolicy, FormattedBookingCancellationPolicy } from './booking-cancellation-policy-model'
import { FormattedImproperBedroom, ImproperBedrooms } from './improper-bedroom'
import { CustomerObjects, FormattedCustomerObjectsTransport } from './customer-objects-model'
import { FormattedOfferTransport, Offer } from '../common/offers-model'
import { PriceFormatter } from '../../hooks/price'
import { Footer, FormattedFooterTransport } from './footer-model'
import { BreadcrumbList, FormattedBreadcrumbTransport } from '../common/breadcrumb-model'
import AdvancedBreadcrumbTransport from '../../transports/common/advanced-breadcrumb-transport'

export interface FormattedPropertyData {
    general: PropertyDetailsGeneralTransport
    id: string
    externalObjectId: string
    title: string
    fullTitle: string
    gallery: GalleryImages[]
    video?: GalleryImages
    objectDescription: PropertyOverview
    description: PropertyDescriptionTransport
    listingConfig: ListingConfigTransport
    isTraumBoostObject: boolean
    trust: Record<string, string | null>
    stats: FormattedStats | null
    surroundings: Array<SurroundingLocation>
    location: Location
    hideExactPosition: boolean
    activities: string[]
    accessibility: FormattedAccessibilityTransport | null
    suitables: FormattedSuitablesMap
    suited: FormattedSuitedMap
    filterFeatMap: FormattedFeatFilterMap
    features: Features
    rooms: FormattedRoomsResult[]
    extendedFeatures: FormattedSuitableFeatures | null
    contact: FormattedContactDetails
    bookingType: FormattedBookingTypeData
    reviews: FormattedRatingTransport
    propertyTypeConfig: PropertyTypeConfig
    notes: FormattedTravelNote[]
    bookingCancellationPolicy: FormattedBookingCancellationPolicy | null
    improperBedrooms: FormattedImproperBedroom | null
    customerObjects: FormattedCustomerObjectsTransport | null
    offers: FormattedOfferTransport | null
    footer: FormattedFooterTransport
    breadcrumbs: FormattedBreadcrumbTransport[]
    propertyRegion: FormattedBreadcrumbTransport | null
}

export class PropertyDetails {
    private readonly translationFunction: TranslationFunction
    private readonly r: RoutingFunction
    private readonly formatDate: FormatDateFunction
    private readonly priceFormatter: PriceFormatter
    private readonly FEATURES_COUNT_TO_SHOW = 15

    constructor(
        translationFunction: TranslationFunction,
        r: RoutingFunction,
        formatDate: FormatDateFunction,
        priceFormatter: PriceFormatter,
    ) {
        this.translationFunction = translationFunction
        this.r = r
        this.formatDate = formatDate
        this.priceFormatter = priceFormatter
    }

    private createFullTitle(label: string, title: string): string {
        if (!title) {
            return label
        }
        if (title.includes(label)) {
            return title
        }
        return `${label} ${title}`
    }

    getData(
        transport: ExposeTransport,
        object?: PropertyObjectTransport,
        listingConfigTransport?: ListingConfigTransport,
        custObjects?: CustomerObjectsTransport,
        breadcrumbs: AdvancedBreadcrumbTransport[] = [],
        bathroomCount = 0,
    ): FormattedPropertyData {
        const general = new PropertyDetailsGeneralModel(transport.objectGeneral, bathroomCount)
        const listingConfig = new ListingConfigModel(listingConfigTransport)
        const propertyType = new PropertyType(transport.objectGeneral.propertyTypeId, this.translationFunction)
        const accessibility = new Accessibility(this.translationFunction).format(general.accessibility)
        const suitables = new SuitablesModel(this.translationFunction).format(transport.objectSuitables)
        const suited = new Suited(this.translationFunction).format(general.suitedIds)
        const filterFeatMap = new FeatFilter(this.translationFunction).format(general.featIds)
        const improperBedrooms = object?.bedrooms
            ? new ImproperBedrooms(this.translationFunction).format(object?.bedrooms)
            : null

        const contact = new ContactFormatter(this.translationFunction).format(transport)
        const fullTitle = this.createFullTitle(propertyType.getLabel(), transport.objectDescription.title)
        const breadcrumbList = new BreadcrumbList(this.translationFunction)
        const gallery = new Gallery(transport.objectImages.images, this.translationFunction, transport.objectVideo)

        return {
            general,
            id: general.tfwObjectId,
            externalObjectId: general.externalObjectId,
            title: transport.objectDescription.title,
            fullTitle,
            gallery: gallery.images,
            video: gallery.video,
            objectDescription: transport.objectDescription,
            description: new PropertyDescription(transport).format(),
            listingConfig,
            isTraumBoostObject: object?.isTraumBoostObject ?? false,
            trust: new Trust(this.translationFunction).format({
                ratings: transport.objectRatings,
                general: transport.objectGeneral,
            }),
            stats: new Stats(this.translationFunction).format(
                general,
                transport.objectRooms.rooms,
                improperBedrooms?.total ?? 0,
            ),
            surroundings: new Surrounding().format(transport.objectLocation),
            location: transport.objectLocation.location,
            hideExactPosition: transport.objectLocation.hideExactPosition,
            activities: transport?.objectActivities?.activities,
            accessibility,
            suitables,
            suited,
            filterFeatMap,
            features: new FeaturesFormatter(this.translationFunction).format(
                general.generalEquipments,
                accessibility,
                suitables,
                suited,
                filterFeatMap,
                object?.bedrooms.total || 0,
                bathroomCount,
                general.persons?.total || 0,
                general.squareMeters || 0,
                improperBedrooms?.total || 0,
                this.FEATURES_COUNT_TO_SHOW,
                true,
                !general.isComboListing || !general.isEntity,
            ),
            rooms: new Rooms(this.translationFunction).format(transport.objectRooms, transport.objectFeatures.features),
            extendedFeatures: new SuitablesModel(this.translationFunction).formatedSuitableFeatures(
                transport.objectSuitables,
            ),
            contact,
            bookingType: new BookingType(this.translationFunction, this.r).format(
                general.bookingType,
                general.tfwObjectId,
                general.providerId,
                general.externalObjectId,
                contact.hasEmail,
                general.traumPaymentsActivated,
            ),
            reviews: new Reviews(this.translationFunction, this.formatDate, this.r).format(
                transport.objectRatings,
                general.providerId,
                transport.objectContact,
                general.tfwObjectId,
            ),
            propertyTypeConfig: propertyType.getConfig(),
            notes: new TravelNotes(this.translationFunction, this.priceFormatter).format(
                transport.objectRentNotes,
                !general.traumPaymentsActivated,
            ),
            bookingCancellationPolicy: new BookingCancellationPolicy().format(general.bookingCancellationPolicy),
            improperBedrooms,
            customerObjects: new CustomerObjects(this.translationFunction, this.r).format(
                custObjects ?? { total: 0, objects: [] },
                general.objectOverviewLink,
            ),
            offers: new Offer(this.translationFunction, this.formatDate).format(transport.objectOffer, null),
            footer: new Footer(this.translationFunction, this.r).format(transport, fullTitle, object),
            breadcrumbs: breadcrumbList.format(breadcrumbs, fullTitle),
            propertyRegion: breadcrumbList.getLastRegionLink(breadcrumbs, fullTitle),
        }
    }
}
